/**
 * Системные уведомления браузера о событиях доски. Работают, пока открыта хотя бы одна вкладка
 * трекера (в том числе фоновая или свёрнутая). Всё считается на клиенте из живых обновлений доски.
 * Настройки — свои у каждого пользователя в этом браузере.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { NotifyPrefs, TicketDto } from '@sync-tracker/shared';
import { formatDuration } from '@/lib/format';
import { router } from '@/router';
import { useBoard } from './board';
import { useMeta } from './meta';
import { usePrefs } from './prefs';
import { timerStartMs } from '@/lib/ticketTimer';

export type NotifyEvent = 'newTicket' | 'sla' | 'nearAbandon' | 'promiseBroken';

export const NOTIFY_EVENTS: { key: NotifyEvent; label: string; hint: string }[] = [
  { key: 'newTicket', label: 'Новый запрос', hint: 'Клиент задал вопрос — на доске появилась карточка.' },
  { key: 'sla', label: 'Ждёт дольше SLA', hint: 'Запрос без ответа дольше первого уровня таймера.' },
  { key: 'nearAbandon', label: 'Скоро станет брошенным', hint: 'Осталось несколько минут до автозакрытия «Брошен».' },
  { key: 'promiseBroken', label: 'Сорвано обещание', hint: '«Сейчас уточню» — и тишина дольше срока обещания.' },
];

export type SoundKind = 'pik' | 'drop' | 'chime';
export const SOUND_KINDS: { value: SoundKind; label: string }[] = [
  { value: 'pik', label: 'Пик' },
  { value: 'drop', label: 'Капля' },
  { value: 'chime', label: 'Колокольчик' },
];

/** Настройки уведомлений — часть личных настроек (см. @sync-tracker/shared prefs.ts). */
type Prefs = NotifyPrefs;

type Permission = NotificationPermission | 'unsupported';
const supported = () => typeof window !== 'undefined' && 'Notification' in window && window.isSecureContext;

interface Pending {
  event: NotifyEvent;
  ticket: TicketDto;
  title: string;
  body: string;
}

export const useNotifications = defineStore('notifications', () => {
  const meta = useMeta();
  const board = useBoard();

  const permission = ref<Permission>(supported() ? Notification.permission : 'unsupported');
  // настройки уведомлений и звука — часть личных настроек пользователя (хранятся на сервере)
  const prefsStore = usePrefs();
  const prefs = computed<Prefs>(() => prefsStore.prefs.notify);

  /** Реально работают: включены пользователем и разрешены браузером. */
  const active = computed(() => prefs.value.enabled && permission.value === 'granted');

  /** Вызывать только из обработчика клика — иначе браузер не покажет запрос разрешения. */
  async function enable() {
    if (!supported()) return false;
    if (Notification.permission === 'default') permission.value = await Notification.requestPermission();
    else permission.value = Notification.permission;
    prefs.value.enabled = permission.value === 'granted';
    if (prefs.value.enabled) unlockSound();
    return prefs.value.enabled;
  }
  function disable() {
    prefs.value.enabled = false;
  }

  // ── Звук: синтезируется в браузере, без файлов ───────────────────────────
  let audio: AudioContext | null = null;
  function unlockSound() {
    try {
      audio ??= new AudioContext();
      if (audio.state === 'suspended') void audio.resume();
    } catch {
      audio = null;
    }
  }
  /** Нота с мягкой атакой и затуханием (без щелчков). */
  function tone(ctx: AudioContext, at: number, freq: number, dur: number, peak: number, type: OscillatorType = 'sine', glideTo?: number) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, at);
    if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, at + dur);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(peak, at + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + dur + 0.02);
  }
  /** Проиграть звук уведомления. kind/volume — для кнопки «Послушать» в профиле. */
  function beep(kind: SoundKind = prefs.value.soundKind, volume = prefs.value.volume) {
    if (!audio) unlockSound();
    if (!audio || audio.state !== 'running') return;
    const v = Math.max(0, Math.min(100, volume)) / 100;
    if (!v) return;
    const t = audio.currentTime + 0.01;
    if (kind === 'pik') {
      // короткий мягкий «пик»: чистый тон и тихий обертон
      tone(audio, t, 1318.5, 0.13, 0.22 * v);
      tone(audio, t, 2637, 0.07, 0.04 * v);
    } else if (kind === 'drop') {
      // «капля»: быстрый спад высоты
      tone(audio, t, 1500, 0.16, 0.22 * v, 'sine', 650);
    } else {
      // «колокольчик»: два тихих тона вверх
      tone(audio, t, 987.8, 0.32, 0.14 * v, 'triangle');
      tone(audio, t + 0.12, 1318.5, 0.42, 0.12 * v, 'triangle');
    }
  }

  // ── Показ ────────────────────────────────────────────────────────────────
  /** Несколько вкладок трекера не должны дублировать одно и то же уведомление. */
  function claim(key: string): boolean {
    try {
      const k = `st-n:${key}`;
      if (localStorage.getItem(k)) return false;
      localStorage.setItem(k, String(Date.now()));
      return true;
    } catch {
      return true;
    }
  }
  function cleanupClaims() {
    try {
      const cutoff = Date.now() - 6 * 3600_000;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k?.startsWith('st-n:') && Number(localStorage.getItem(k)) < cutoff) localStorage.removeItem(k);
      }
    } catch {
      /* ignore */
    }
  }

  function show(title: string, body: string, tag: string, ticketId: string | null) {
    // свой звук браузер разрешает только после первого клика по странице; пока его нет —
    // пусть звучит системное уведомление (его браузер не блокирует)
    unlockSound();
    const ownSound = prefs.value.sound && audio?.state === 'running';
    const n = new Notification(title, { body, tag, icon: '/favicon.svg', silent: !prefs.value.sound || ownSound });
    n.onclick = () => {
      window.focus();
      if (ticketId) void router.push({ path: '/board', query: { ticket: ticketId } });
      n.close();
    };
    if (prefs.value.sound) beep();
  }

  let queue: Pending[] = [];
  let flushTimer: number | null = null;
  function push(p: Pending) {
    if (!claim(`${p.event}:${p.ticket.id}`)) return;
    queue.push(p);
    // пачка событий за полторы секунды (переподключение, массовый импорт) — одним уведомлением
    flushTimer ??= window.setTimeout(flush, 1500);
  }
  function flush() {
    flushTimer = null;
    const items = queue;
    queue = [];
    // пользователь и так смотрит на трекер
    if (!items.length) return;
    if (document.visibilityState === 'visible' && document.hasFocus()) {
      // вы и так смотрите на трекер: окошко не показываем, но по желанию тихо «пикаем»
      if (prefs.value.sound && prefs.value.soundWhenFocused) beep();
      return;
    }
    if (items.length === 1) {
      const p = items[0]!;
      show(p.title, p.body, `st-${p.event}-${p.ticket.id}`, p.ticket.id);
      return;
    }
    const lines = items.slice(0, 4).map((p) => `${p.title} — ${p.ticket.chatTitle}`);
    if (items.length > 4) lines.push(`…и ещё ${items.length - 4}`);
    show(`Событий на доске: ${items.length}`, lines.join('\n'), 'st-batch', null);
  }

  // ── Правила ──────────────────────────────────────────────────────────────
  const clip = (s: string | null, n = 120) => (!s ? '' : s.length > n ? `${s.slice(0, n - 1)}…` : s);
  const columnTitle = (t: TicketDto) => meta.column(t.columnKey)?.title ?? t.columnKey;
  const wanted = (event: NotifyEvent, t: TicketDto) =>
    active.value &&
    prefs.value.events[event] &&
    t.state !== 'closed' &&
    (!prefs.value.columns.length || prefs.value.columns.includes(t.columnKey));

  function onTicket(t: TicketDto, prev: TicketDto | undefined) {
    if (!prev) {
      // свежий вопрос, а не старая карточка, всплывшая при обновлении
      if (wanted('newTicket', t) && Date.now() - new Date(t.firstMessageAt).getTime() < 10 * 60_000) {
        push({ event: 'newTicket', ticket: t, title: `Новый запрос · ${columnTitle(t)}`, body: `${t.chatTitle}\n${clip(t.firstMessageText) || 'вложение'}` });
      }
      return;
    }
    if (!prev.levelKey && t.levelKey && !t.firstResponseAt && wanted('sla', t)) {
      const level = meta.levels.find((l) => l.key === t.levelKey);
      push({
        event: 'sla',
        ticket: t,
        title: `Без ответа дольше ${formatDuration(level?.afterSec ?? meta.levels[0]?.afterSec ?? 60)}`,
        body: `${t.chatTitle} · ${columnTitle(t)}\n${clip(t.firstMessageText)}`,
      });
    }
    if (!prev.promiseBrokenAt && t.promiseBrokenAt && wanted('promiseBroken', t)) {
      push({
        event: 'promiseBroken',
        ticket: t,
        title: 'Сорвано обещание',
        body: `${t.chatTitle}\n${t.promisedBy ? `${t.promisedBy}: ` : ''}обещали вернуться, но ответа нет`,
      });
    }
  }

  /** «Скоро брошен» — по часам, а не по событию: срабатывает, когда карточка пересекает порог. */
  let lastCheck = Date.now();
  function checkNearAbandon() {
    const now = Date.now();
    const abandonSec = meta.data?.timers.abandonAfterSec;
    if (abandonSec && active.value && prefs.value.events.nearAbandon) {
      const leadSec = Math.min(600, Math.round(abandonSec / 4));
      for (const t of board.open) {
        const start = timerStartMs(t, meta.offHours);
        if (start == null) continue; // карточка из нерабочего времени без таймера брошенной не станет
        const at = start + (abandonSec - leadSec) * 1000;
        if (at > lastCheck && at <= now && wanted('nearAbandon', t)) {
          push({
            event: 'nearAbandon',
            ticket: t,
            title: `Через ${formatDuration(leadSec)} станет брошенным`,
            body: `${t.chatTitle} · ${columnTitle(t)}\n${clip(t.firstMessageText)}`,
          });
        }
      }
    }
    lastCheck = now;
  }

  function test() {
    if (!active.value) return;
    show('Уведомления работают', 'Так будут выглядеть события доски.', 'st-test', null);
  }

  let wired = false;
  function wire() {
    if (wired) return;
    wired = true;
    board.onChange(onTicket);
    window.setInterval(checkNearAbandon, 20_000);
    // разрешение могли поменять в настройках сайта
    document.addEventListener('visibilitychange', () => {
      if (supported()) permission.value = Notification.permission;
    });
    cleanupClaims();
    // браузер не даёт играть звук до первого действия на странице — «разблокируем» по первому клику/клавише
    const unlock = () => {
      unlockSound();
      if (audio?.state === 'running') {
        window.removeEventListener('pointerdown', unlock, true);
        window.removeEventListener('keydown', unlock, true);
      }
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
  }

  /** Послушать звук из настроек (вызывается кликом — звук точно разрешён). */
  function preview(kind?: SoundKind) {
    unlockSound();
    window.setTimeout(() => beep(kind), 30);
  }

  return { permission, prefs, active, enable, disable, test, wire, preview };
});
